import { IEntityUser } from '@boma/client-shared';
import { EntityType } from '@boma/shared/entities';
import { IProduct } from '@boma/website/shared/entities';
import { ICircle } from '../models/i-circle';
import { IMultiPolygon } from '../models/i-multi-polygon';
import { IGeography, ILocation, ILocationSimple } from './i-location';

export interface ICustomer {
  id: string;
  name: string;
  type: EntityType;
}

export interface ICustomerEntity {
  id: string;
  name: string;
  type: EntityType;
  registered: true;
  sector: string;
  registeredName: string;
  tradingName: string;
  registrationNumber: string;
  taxNumber: string;
  contactNumber: string;
  physicalAddressLineOne: string;
  physicalAddressLineTwo: string;
  physicalAddressCity: string;
  postalAddressLineOne: string;
  postalAddressLineTwo: string;
  postalAddressCity: string;
  postalAddressPostalCode: string;
  websiteAddress: string;
  active: true;
  notes: string;
  isAllowedToPurchaseOnCredit: boolean;
  locations: ILocationSimple;
  selected?: boolean;
}

export interface ICustomerCreate {
  name: string;
  surname: string;
  contactNumber: string;
  latitude?: number;
  longitude?: number;
}

export interface ICustomerCreateUnregistered {
  name: string;
  surname: string;
  contactNumber: string;
  businessName: string;
  defaultLocationName: string;
  longitude: number;
  latitude: number;
  sector: string;
  registrationNumber: string;
  salesTaxNumber: string;
  physicalAddressLineOne: string;
  physicalAddressLineTwo: string;
  physicalAddressCity: string;
  postalAddressLineOne: string;
  postalAddressLineTwo: string;
  postalAddressPostalCode: string;
  postalAddressCity: string;
}

export interface ICustomerBulkCreate {
  firstName: string;
  surname: string;
  email: string;
  name: string;
  entityType: EntityType;
  defaultLocationName: string;
  registeredName: string;
  tradingName: string;
  registrationNumber: string;
  taxNumber: string;
  contactNumber: string;
  physicalAddressLineOne: string;
  physicalAddressLineTwo: string;
  physicalAddressCity: string;
  postalAddressLineOne: string;
  postalAddressLineTwo: string;
  postalAddressCity: string;
  postalAddressPostalCode: string;
  country: string;
  yearEnd: string;
  weekStart: string;
  websiteAddress: string;
  active: boolean;
  sector: string;
}

export interface ICustomerUpdate {
  notes: string;
  isAllowedToPurchaseOnCredit: boolean;
}

export interface ICustomerLocation {
  customerEntityId: string;
  customerName: string;
  locationId: string;
  locationName: string;
  claimed: boolean;
  point: {
    type: string;
    coordinates: number[];
  };
}

export interface ILocationCustomerCreateLocation {
  countryId: string;
  name: string;
  address: ICustomerLocationAddress;
  isEntireCountry: boolean;
  active: boolean;
  point?: IGeography;
  notificationEmailAddress: string;
  contactPersonName: string;
  contactPersonNumber: string;
}

export interface ILocationCustomerUpdateLocation
  extends ILocationCustomerCreateLocation {
  id: string;
}

export interface ICustomerLocationAddress {
  line1: string;
  line2: string;
  line3: string;
  line4: string;
  town: string;
  province: string;
  postalCode: string;
  locationId?: string;
}

export class LoadCustomerLocationsParams {
  constructor(
    public entityId: string,
    public page = 1,
    public perPage = 25
  ) {}
}

export class LoadCustomerLocationsRequest {
  constructor(
    // Order creation start
    public startDate?: Date,
    // Order creation cutoff
    public endDate?: Date,
    public locationIds: string[] = null,
    public customerLocationIds: string[] = null,
    public customerIds: string[] = null,
    public searchBoundaries: IMultiPolygon = null,
    public circleSearchBoundaries: ICircle = null
  ) {}
}

export class LoadCustomersRequest {
  constructor(
    public entityId: string,
    public query = '',
    public page = '1',
    public perPage = '25',
    public customerType = 'All',
    public searchTarget: 'Vendor' | 'Customer' = 'Customer'
  ) {}
}

export interface ICustomerLocationProductPricingResponse {
  entity: IEntityUser;
  customerPricingDetails: ICustomerLocationProducts[];
}

export interface ICustomerLocationProducts {
  id: string;
  entityCustomer: ICustomer;
  locationProductPricing: ICustomerLocationProductDetails[];
}

export interface ICustomerLocationProductDetails {
  id: string;
  product: IProduct;
  location: ILocation;
  price: number;
  minimumOrderQuantity: number;
  specialInstructions: string;
  acceptCashOnDelivery: boolean;
  acceptBankTransfers: boolean;
  maximumOrderQuantity: number;
  enforceStockChecks: boolean;
  confirmAllOrders: boolean;
  validFrom?: string;
  validUntil?: string;
}

export interface ICreateOrUpdateCustomerLocationProductPricingRequest {
  entityId: string;
  locationProductPricing: ICustomerLocationProductPricingRequest[];
}

export interface ICustomerLocationProductPricingRequest {
  // Leaving this null will create a new pricing tier
  id?: string;
  entityId: string;
  locationId: string;
  productId: string;
  price: number;
  minimumOrderQuantity: number;
  specialInstructions: string;
  acceptCashOnDelivery: boolean;
  acceptBankTransfers: boolean;
  maximumOrderQuantity: number;
  enforceStockChecks: boolean;
  confirmAllOrders: boolean;
  validFrom?: string;
  validUntil?: string;
}

export interface BasicPricing {
  price?: number;
  minimumOrderQuantity: number;
  specialInstructions: string;
  acceptCashOnDelivery: boolean;
  acceptBankTransfers: boolean;
  maximumOrderQuantity: number;
  enforceStockChecks: boolean;
  confirmAllOrders: boolean;
  validFrom?: string;
  validUntil?: string;
}

export interface ICustomerSummaryRequest {
  locationId: string;
  customerId: string;
}

export interface ICustomerSummaryResponse {
  customerId: string;
  logo: {
    url: string;
    height: number;
  };
  tradingName: string;
  locationId: string;
  locationName: string;
  totalSalesValue: number;
  totalSales: number;
  latestOrders: [
    {
      orderId: string;
      value: number;
      createdAt: string;
      user: {
        id: string;
        name: string;
        surname: string;
      };
      status: string;
      statusCreatedAt: string;
      origin: number;
    },
  ];
}
